import { Component, OnInit, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { RemoveEvent } from "@progress/kendo-angular-grid";

import { FormFieldExpression } from '../../../../models/field-conditional-logic/form-field-expression.model';
import { FormFieldAction } from '../../../../models/field-conditional-logic/form-field-action.model';
import { Form } from '../../../../models/form-builder/form.model';
import { ConditionalLogicRuleOperator } from '../../../../models/field-conditional-logic/rule-operator.model';
import { FieldDefinitionService } from '../../../../services/field-definition.service';
import { FormField } from '../../../../models/form-builder/form-field.model';
import { FieldDefinition } from '../../../../models/form-builder/field-definition.model';
//import { IFieldDefinitionLogic } from '../../../../interfaces/ifield-definition-logic.interface';
import { FieldConditionalLogicService, FormFieldActionValue, TargetFieldDefaultAttributeValue } from '../../../../services/field-conditional-logic.service';
import { FieldConditionalLogicActionNameEnum, FieldConditionalLogicTargetFieldDefaultAttrs } from '../../../../models/field-conditional-logic/enums';
//import { FormFieldUtil } from '../../../../utility-classes/form-field.util';
import { CurrentRuleCondition, CurrentConditionalLogicRuleInfo, CurrentRuleAction, AllCurrentRuleActions, AllFieldInfo, IRulesByTargetFieldIdAndAction, MultipleFieldsMetadataFieldInfo } from '../../../../models/field-conditional-logic/conditional-logic-editor-classes';
import { IFormBuilderAssistant } from '../../form-builder-assistant/iform-builder-assistant.interface';
import { FormTemplateInstancesFieldType, GridFieldType } from '../../../../models/form-builder/form-field-types';
//import { FormTemplateInstancesConfig } from '../../../../models/form-template-instances/form-template-instances-config.model';
//import { FormTemplatePropertyInfo } from '../../../../models/form-template-instances/form-template-property-info';
import { FormBuilderService } from '../../../../services/form-builder.service';

// Note:  the following class allows us to track component state information,
//        allowing us to compute values only when necessary.
class ComponentControlStateInformation {
    public recomputeAddRuleConditionEnabled: boolean = true;

    public addRuleConditionEnabled: boolean = false;

    public constructor() {
    }
}

@Component({
    selector: 'app-field-conditional-logic',
    templateUrl: './field-conditional-logic.component.html',
    styleUrls: ['./field-conditional-logic.component.scss'],
    standalone: false
})
export class FieldConditionalLogicComponent implements OnInit {
    // Properties.
    private currentRuleAction: CurrentRuleAction = null; // new CurrentRuleAction();
    private allRuleActions: AllCurrentRuleActions = null; // new AllCurrentRuleActions();
    private allFieldInfo: AllFieldInfo = null;
    private componentControlStateInformation: ComponentControlStateInformation = new ComponentControlStateInformation();

    // Constructor.
    public constructor(public dialogRef: MatDialogRef<FieldConditionalLogicComponent>,
        @Inject(MAT_DIALOG_DATA) private data,
        private fieldDefinitionService: FieldDefinitionService,
        private fieldConditionalLogicService: FieldConditionalLogicService,
        private formBuilderService: FormBuilderService)
    {
        let formTemplate: Form = <Form>this.data.form;
        this.currentRuleAction = new CurrentRuleAction(formTemplate);

        this.allRuleActions = new AllCurrentRuleActions(this.fieldConditionalLogicService);
    }

    // Life cycle methods.
    public ngOnInit(): void {
        this.fieldDefinitionService.getMetadataFields().then(metadataFields => {
            let currentForm: Form = <Form>this.data.form;
            this.allFieldInfo = new AllFieldInfo(currentForm.formFields, metadataFields);

            this.loadRuleActions();
        });
    }

    private loadRuleActions(): void {
        let currentForm: Form = <Form>this.data.form;
        if (currentForm.conditionalLogicRules == null)
            currentForm.conditionalLogicRules = [];
        else {
            let existingRules: FormFieldExpression[] = this.fieldConditionalLogicService.getFormConditionalLogicExpr(this.data.form);
            let metadataFormFields: FormField[] = this.allFieldInfo.metadataFieldsAsFauxFormFields();
            let availableOperators: ConditionalLogicRuleOperator[] = this.fieldConditionalLogicService.getAvailableFieldOperators();
            let ruleActions: CurrentRuleAction[] = FieldConditionalLogicComponent.convertExistingRuleToSingleActionRules(currentForm, metadataFormFields, existingRules, availableOperators, this.formBuilderService);
            this.allRuleActions.setRuleActions(ruleActions);
        }
    }

    // Define methods called by my HTML code.
    public get EditRuleTitle(): string {
        return this.currentRuleAction.existingRule != null ? 'Existing Rule Action' : 'New Rule Action';
    }

    public get FormSourceFields(): FormField[] {
        //return this.allFieldInfo.AllFieldsSortedByNameAscending;
        let allFields: FormField[] = this.allFieldInfo.AllFieldsSortedByNameAscending;
        let formBuilderAssistant: IFormBuilderAssistant = this.data.formBuilderAssistant;
        let sourceFields = formBuilderAssistant != null ? formBuilderAssistant.filterConditionalLogicSourceFields(this.fieldDefinitionService, allFields) : allFields;
        return sourceFields;
    }

    public get TargetFieldIds(): number[] {
        return this.currentRuleAction.targetFieldIds;
    }
    public set TargetFieldIds(value: number[]) {
        this.currentRuleAction.targetFieldIds = value;
    }
    public get TargetFieldIdsEmpty(): boolean {
        return this.currentRuleAction.targetFieldIds.length == 0;
    }
    public get RequiredChildFieldIsEmpty(): boolean {
        let requiredChildFieldIsEmpty: boolean = false;

        if (this.TargetFieldIsSingleGrid && (this.currentRuleAction.childFormFieldId == 0))
            requiredChildFieldIsEmpty = true;

        return requiredChildFieldIsEmpty;
    }
    public get TargetFieldIsSingleGrid(): boolean {
        // Note:  this method is true when a) only a single target field is selected and b) that single field is a grid.
        let isSingleGrid: boolean = false;
        if (this.currentRuleAction.targetFormFields.length == 1) {
            let fieldDefinitionClassName: string = this.currentRuleAction.targetFormFields[0].fieldDefinitionClassName;
            if ((fieldDefinitionClassName == FormTemplateInstancesFieldType) || (fieldDefinitionClassName == GridFieldType)) 
                isSingleGrid = true;
        }
        return isSingleGrid;
    }
    public get TargetFieldAvailableChildFields(): FormField[] {
        /*
        let childFields: FormField[] = [];

        if (this.currentRuleAction.availableChildFormFields != null) {
            childFields = this.currentRuleAction.availableChildFormFields;
        }
        else if (this.TargetFieldIsSingleGrid) {
            let fieldDefinitionClassName: string = this.currentRuleAction.targetFormFields[0].fieldDefinitionClassName;

            if ((fieldDefinitionClassName == FormTemplateInstancesFieldType) && (this.currentRuleAction.targetFormFields[0].formTemplateId > 0)) {
                if (!this.currentRuleAction.fetchingChildFormFields) {
                    this.currentRuleAction.fetchingChildFormFields = true;

                    this.formBuilderService.get(this.currentRuleAction.targetFormFields[0].formTemplateId).then(formTemplate => {
                        if (formTemplate.formFields != null) {
                            this.currentRuleAction.availableChildFormFields = formTemplate.formFields;
                            childFields = formTemplate.formFields;

                            this.currentRuleAction.fetchingChildFormFields = false;
                        }
                    });
                }
            } else if ((fieldDefinitionClassName == GridFieldType) && (this.currentRuleAction.targetFormFields[0].childFormFields != null)) {
                childFields = this.currentRuleAction.targetFormFields[0].childFormFields
            }
        }

        return childFields;
        */
        return this.currentRuleAction.availableChildFormFields;
    }
    public get TargetFieldChildFieldId(): number {
        return this.currentRuleAction.childFormFieldId;
    }
    public set TargetFieldChildFieldId(childFieldId: number) {
        this.currentRuleAction.childFormFieldId = childFieldId;
    }
    public getChildFieldDisplayName(childField: FormField): string {
        return (childField.displayName != null) && (childField.displayName.trim() != '') ? childField.displayName : childField.name;
    }

    public get TargetFieldDefaultAttributes(): string {
        return this.currentRuleAction.targetFieldDefaultAttributes;
    }
    public set TargetFieldDefaultAttributes(value: string) {
        this.currentRuleAction.targetFieldDefaultAttributes = value;
    }

    public get TargetFieldAction(): string {
        return this.currentRuleAction.targetFieldAction;
    }
    public set TargetFieldAction(value: string) {
        this.currentRuleAction.targetFieldAction = value;
    }
    public get TargetFieldActionIsApplyFilter(): boolean {
        return this.currentRuleAction.targetFieldAction == FieldConditionalLogicActionNameEnum.ApplyFilter;
    }
    public get TargetFieldActionAppliesToGrids(): boolean {
        // 08-07-2024 note:  at the moment, all target actions can apply to grids.
        return true;
    }

    public getFieldName(formField: FormField): string {
        //return formField != null ? FormFieldUtil.getFieldName(formField) : '';
        return formField != null ? formField.name : ''; // Per VNEXT-1387:  show internal names for target fields.
    }

    public getAvailableOperators(dataItem: CurrentRuleCondition): ConditionalLogicRuleOperator[] {
        let availableOperators: ConditionalLogicRuleOperator[] = [];

        if (dataItem.sourceFormField != null) {
            let sourceField: FormField = dataItem.sourceFormField;

            let fieldDefinition: FieldDefinition = this.fieldDefinitionService.getFieldDefinition(sourceField.fieldDefinitionClassName);
            //let fieldLogic: IFieldDefinitionLogic = fieldDefinition.customLogicHandler;

            if (fieldDefinition.customLogicHandler.isATypeOfGridField(sourceField)) {
                // We are dealing with a type of grid field.
                //let childFormField: FormField = sourceField.childFormFields != null ? sourceField.childFormFields.find(cff => cff.id == dataItem.childFieldId);
                if (dataItem.sourceFieldChildField != null) {
                    fieldDefinition = this.fieldDefinitionService.getFieldDefinition(dataItem.sourceFieldChildField.fieldDefinitionClassName);

                    availableOperators = fieldDefinition.customLogicHandler.getAvailableOperatorsFor(dataItem.sourceFormField);
                }
            } else {
                // We are dealing with an ordinary field.
                availableOperators = fieldDefinition.customLogicHandler.getAvailableOperatorsFor(dataItem.sourceFormField);
            }
        }

        return availableOperators;
    }

    public get CurrentRuleConditions(): CurrentRuleCondition[] {
        return this.currentRuleAction.conditions;
    }

    public get FieldDefsLoaded(): boolean {
        return this.allFieldInfo != null;
    }
    public get FormTargetFields(): FormField[] {
        let availableTargetFields: FormField[] = [];

        // Get form fields.
        let currentForm: Form = <Form>this.data.form;
        if (currentForm.formFields != null) {
            availableTargetFields = currentForm.formFields.filter(ff => !ff.isMetadataField);
            FormField.sortFormFieldsAlphabetically(availableTargetFields);
        }            

        return availableTargetFields;
    }
    public get AvailableTargetFieldDefaultAttributes(): FieldConditionalLogicTargetFieldDefaultAttrs[] {
        return this.fieldConditionalLogicService.getAvailableTargetFieldDefaultAttributes();
    }
    public get AvailableTargetFieldDefaultAttributeValues(): TargetFieldDefaultAttributeValue[] {
        return this.fieldConditionalLogicService.getAvailableTargetFieldDefaultAttributeValues();
    }

    public get AvailableFieldActions(): FieldConditionalLogicActionNameEnum[] {
        return this.fieldConditionalLogicService.getAvailableFormFieldActions();
    }
    public get AvailableFieldActionValues(): FormFieldActionValue[] {
        return this.fieldConditionalLogicService.getAvailableFormFieldActionValues();
    }

    public get AddRuleConditionEnabled(): boolean {
        let enable: boolean = this.componentControlStateInformation.addRuleConditionEnabled;

        if (this.componentControlStateInformation.recomputeAddRuleConditionEnabled) {
            this.componentControlStateInformation.recomputeAddRuleConditionEnabled = false;

            setTimeout(() => {
                // Compute the button's enabled state in the background to avoid the dreaded
                // "ExpressionChangedAfterItHasBeenCheckedError: Expression has changed after it was checked" error.
                this.recomputeAddRuleConditionEnabled();
            }, 0);
        }

        return enable;
    }
    private recomputeAddRuleConditionEnabled(): void {
        this.componentControlStateInformation.addRuleConditionEnabled = false;

        // Handle two cases:  a) a single grid field is selected as the target field and b) all other conditions.
        if (this.TargetFieldIsSingleGrid) {
            this.componentControlStateInformation.addRuleConditionEnabled = //(this.TargetFieldChildFieldId > 0) && // We need to have a child field selected.
                (this.currentRuleAction.targetFieldAction != FieldConditionalLogicActionNameEnum.NoAction); // We need to have a valid action selected.
        } else {
            this.componentControlStateInformation.addRuleConditionEnabled = (!this.TargetFieldIdsEmpty) // We need to have a target field.
                && (this.currentRuleAction.targetFieldAction != FieldConditionalLogicActionNameEnum.NoAction); // We need to have a valid action selected.
        }
    }
    public get SaveCurrentRuleEnabled(): boolean {
        let enabled: boolean = (!this.TargetFieldIdsEmpty) && (this.TargetFieldAction != FieldConditionalLogicActionNameEnum.NoAction) && this.currentRuleAction.RuleConditionsValid;
        return enabled;
    }
    public get ExistingFieldConditionalLogicRules(): FormFieldExpression[] {
        return this.fieldConditionalLogicService.getFormConditionalLogicExpr(this.data.form);
    }
    public get ExistingRulesAsCurrentRuleActions(): CurrentRuleAction[] {
        return this.allRuleActions.getRuleActions();
    }

    public get RowSelectable(): boolean {
        return true;
    }

    public get SelectedKeys(): number[] {
        return [];
    }
    public set SelectedKeys(value: number[]) {
        // Note:  this method presently does nothing by design.
    }

    public get CommandButtonDisabled(): boolean {
        return false;
    }

    public get NoRuleDefined(): boolean {
        return false;
    }

    public get DisableOperatorValueInput(): boolean {
        let disable: boolean = false;
        if ((this.currentRuleAction != null) && (!this.currentRuleAction.requiresOperandValue))
            disable = true;
        return disable;
    }

    public getTargetFieldName(dataItem: FormFieldExpression): string {
        let targetFieldName: string = 'No Field Selected';
        if ((dataItem.actions != null) && (dataItem.actions.length > 0)) {
            let currentForm: Form = <Form>this.data.form;
            let targetField: FormField = CurrentConditionalLogicRuleInfo.findFormFieldByName(currentForm, dataItem.actions[0].targetFieldName);
            targetFieldName = this.getFieldName(targetField);

            let mapOfTargetFields = {
                name: targetField.name
            };
            for (let index: number = 1; index < dataItem.actions.length; index++) {
                let formFieldAction: FormFieldAction = dataItem.actions[index];
                targetField = CurrentConditionalLogicRuleInfo.findFormFieldByName(currentForm, dataItem.actions[index].targetFieldName);
                if (mapOfTargetFields[targetField.name] == null)
                    mapOfTargetFields[targetField.name] = targetField.name;
            }
            let numTargetFields: number = Object.keys(mapOfTargetFields).length;
            if (numTargetFields > 1)
                targetFieldName += numTargetFields == 2 ? ' + 1' : `+ ${numTargetFields - 1}`
        }
        return targetFieldName;
    }
    public getSourceFieldName(dataItem: FormFieldExpression): string {
        let sourceFieldName: string = 'No Field Selected';

        let currentForm: Form = <Form>this.data.form;
        let sourceField: FormField = CurrentConditionalLogicRuleInfo.findFormFieldByName(currentForm, dataItem.sourceFieldName);
        sourceFieldName = this.getFieldName(sourceField);

        return sourceFieldName;
    }
    public getRuleConditionText(dataItem: FormFieldExpression): string {
        let currentForm: Form = <Form>this.data.form;
        let ruleConditionText: string = dataItem.getRuleConditionText(currentForm, this.fieldDefinitionService, false);
        return ruleConditionText;
    }
    public getTargetFieldNameFrom(ruleAction: CurrentRuleAction): string {
        let formField: FormField = ruleAction.targetFormFields[0];

        let targetFieldName: string = this.getFieldName(formField);
        if ((ruleAction.childFieldName != null) && (ruleAction.childFieldName.trim() != ''))
            targetFieldName += ` -- ${ruleAction.childFieldName}`;

        return targetFieldName;
    }
    public getSourceFieldNameFrom(ruleAction: CurrentRuleAction): string {
        if ((ruleAction.conditions == null) || (ruleAction.conditions.length == 0))
            throw `FieldConditionalLogicComponent.getSourceFieldNameFrom():  encountered a rule without a condition.`;

        let firstCondition: CurrentRuleCondition = ruleAction.conditions[0];
        let sourceFieldName: string = this.getFieldName(firstCondition.sourceFormField);

        if (ruleAction.conditions.length > 1)
            sourceFieldName += ruleAction.conditions.length == 2 ? ' +1 field' : ` +${ruleAction.conditions.length - 1} fields`;

        return sourceFieldName;
    }
    public getRuleConditionTextFrom(ruleAction: CurrentRuleAction): string {
        if ((ruleAction.conditions == null) || (ruleAction.conditions.length == 0))
            throw `FieldConditionalLogicComponent.getRuleConditionTextFrom():  encountered a rule without a condition.`;

        let firstCondition: CurrentRuleCondition = ruleAction.conditions[0];
        let operatorInfo = this.fieldConditionalLogicService.getFieldOperatorFromId(firstCondition.comparisonOperatorId);
        let fieldDefinition: FieldDefinition = this.fieldDefinitionService.getFieldDefinition(firstCondition.sourceFormField.fieldDefinitionClassName);
        let comparisonValueText: string = fieldDefinition.customLogicHandler.displayValueWithQuotationMarks() ? `'${firstCondition.comparisonValue}'` : firstCondition.comparisonValue;
        let ruleConditionText: string = operatorInfo.requiresOperandValue ? `${operatorInfo.symbol} ${comparisonValueText}` : `${operatorInfo.symbol}`;

        if (ruleAction.conditions.length > 1)
            ruleConditionText += ruleAction.conditions.length == 2 ? ' +1 condition' : ` +${ruleAction.conditions.length - 1} conditions`;

        return ruleConditionText;
    }

    public getRuleActionTextFrom(ruleAction: CurrentRuleAction): string {
        let ruleActionText: string = ruleAction.targetFieldAction != null ? ruleAction.targetFieldAction.replace(/\|/g, ', ') : '';
        return ruleActionText;
    }

    public sourceFieldIsGrid(ruleCondition: CurrentRuleCondition): boolean {
        let fieldIsGrid: boolean = false;

        if (ruleCondition.sourceFormField != null) {
            let fieldDef: FieldDefinition = this.fieldDefinitionService.getFieldDefinition(ruleCondition.sourceFormField.fieldDefinitionClassName);
            fieldIsGrid = fieldDef.customLogicHandler.isATypeOfGridField(ruleCondition.sourceFormField);
        }

        return fieldIsGrid;
    }
    public getAvailableGridColumns(ruleCondition: CurrentRuleCondition): FormField[] {
        let gridColumns: FormField[] = [];

        if ((ruleCondition.sourceFormField != null) && (ruleCondition.sourceFormField.childFormFields != null)) {
            gridColumns = ruleCondition.sourceFormField.childFormFields;
        }

        return gridColumns;
    }
    public getGridColumnName(gridColDef: FormField): string {
        let columnName: string = (gridColDef.displayName != null) && (gridColDef.displayName.trim() != '') ? gridColDef.displayName : gridColDef.name;
        return columnName;
    }

    // Handle control events.
    public sourceFieldChanged(dataItem: CurrentRuleCondition, eventData: any): void {   
        if (dataItem.sourceFieldId != 0) {
            if (dataItem.sourceFieldId > 0) {
                // This is an ordinary form field.
                let currentForm: Form = <Form>this.data.form;
                dataItem.sourceFormField = CurrentConditionalLogicRuleInfo.findFormFieldById(currentForm, this.allFieldInfo.metadataFieldsAsFauxFormFields(), dataItem.sourceFieldId);
            } else {
                // This is a metadata 'faux' form field.
                let metadataFields: FormField[] = this.allFieldInfo.metadataFieldsAsFauxFormFields();
                dataItem.sourceFormField = metadataFields.find(ff => ff.id == dataItem.sourceFieldId);
            }
        } else
            dataItem.sourceFormField = null;
    }
    public childFieldNameChanged(dataItem: CurrentRuleCondition, ignoredChildFieldName: string): void {
        //dataItem.sourceFieldChildField = null;

        if ((dataItem.sourceFieldChildFieldId > 0) && (dataItem.sourceFormField != null) && (dataItem.sourceFormField.childFormFields != null)) {
            /*
            dataItem.sourceFieldChildField = null; // Note:  do this before the following line to avoid copying one form field onto another.
            //dataItem.sourceFieldChildField = dataItem.sourceFormField.childFormFields.find(cff => cff.id = dataItem.sourceFieldChildFieldId);
            let childField: FormField = dataItem.sourceFormField.childFormFields.find(cff => cff.id = dataItem.sourceFieldChildFieldId);
            //dataItem.sourceFieldChildField = <FormField>childField.clone();
            dataItem.sourceFieldChildField = new FormField();
            dataItem.sourceFieldChildField.id = childField.id;
            dataItem.sourceFieldChildField.name = childField.name;
            dataItem.sourceFieldChildField.displayName = childField.displayName;
            dataItem.sourceFieldChildField.fieldDefinitionClassName = childField.fieldDefinitionClassName;
            */
            if (dataItem.sourceFieldChildField != null)
                dataItem.sourceFieldChildFieldName = dataItem.sourceFieldChildField.name;
        }            
    }
    public comparisonOperatorChanged(operatorId: number): void {
        // Lookup information about this operator.
        let operatorInfo: ConditionalLogicRuleOperator = this.fieldConditionalLogicService.getFieldOperatorFromId(operatorId);
        let disableValueInput: boolean = (operatorInfo != null) && (!operatorInfo.requiresOperandValue);
        if (this.currentRuleAction != null)
            this.currentRuleAction.requiresOperandValue = !disableValueInput;
    }
    public comparisonValueChanged(eventData: any): void {
        // Note:  this method is a NOOP by design.
    }
    public targetFieldChanged(nullNotUsedDataItem: CurrentRuleAction, notUsedEventData: any): void {
        // Reset the flag that makes us recompute whether the "Add Condition" button is enabled or not.
        this.componentControlStateInformation.recomputeAddRuleConditionEnabled = true;

        this.currentRuleAction.availableChildFormFields = []; // Start out with this assumption.

        // Handle logic related to selecting a target field.
        if (!this.TargetFieldIdsEmpty) {
            // Update this.currentRuleAction.targetFormFields, the selected target field(s).
            let currentForm: Form = <Form>this.data.form;
            this.currentRuleAction.targetFormFields = CurrentConditionalLogicRuleInfo.findFormFieldsById(currentForm, this.allFieldInfo.metadataFieldsAsFauxFormFields(), this.currentRuleAction.targetFieldIds);

            // Set the available target child form fields, if applicable.
            if (this.TargetFieldIsSingleGrid) {
                // We have some sort of grid.
                let fieldDefinitionClassName: string = this.currentRuleAction.targetFormFields[0].fieldDefinitionClassName;

                if ((fieldDefinitionClassName == FormTemplateInstancesFieldType) && (this.currentRuleAction.targetFormFields[0].formTemplateId > 0)) {
                    // We have a forms grid (the type used on site home pages), so fetch its child fields.
                    if (!this.currentRuleAction.fetchingChildFormFields) {
                        this.currentRuleAction.fetchingChildFormFields = true;

                        this.formBuilderService.get(this.currentRuleAction.targetFormFields[0].formTemplateId).then(formTemplate => {
                            if (formTemplate.formFields != null) {
                                this.currentRuleAction.availableChildFormFields = formTemplate.formFields;
                                //childFields = formTemplate.formFields;

                                this.currentRuleAction.fetchingChildFormFields = false;
                            }
                        });
                    }
                } else if ((fieldDefinitionClassName == GridFieldType) && (this.currentRuleAction.targetFormFields[0].childFormFields != null)) {
                    // We have an ordinary data grid.
                    let childFields: FormField[] = this.currentRuleAction.targetFormFields[0].childFormFields
                    this.currentRuleAction.availableChildFormFields = childFields;
                }
            }
        } else {
            this.currentRuleAction.targetFormFields = [];
        }

        //this.currentRuleAction.availableChildFormFields = null;
    }
    public targetFieldChildFieldIdChanged(eventData: any): void {
        // Update the child field name if possible.
        this.currentRuleAction.childFieldName = '';
        if ((this.currentRuleAction.childFormFieldId > 0) && (this.currentRuleAction.availableChildFormFields != null)) {
            let childField: FormField = this.currentRuleAction.availableChildFormFields.find(cff => cff.id == this.currentRuleAction.childFormFieldId);
            if (childField != null)
                this.currentRuleAction.childFieldName = childField.name;
        }

        // Reset any state-related flags.
        this.componentControlStateInformation.recomputeAddRuleConditionEnabled = true;
    }
    public targetFieldDefaultAttributesChanged(eventData: any): void {
        // Note:  this method is a NOOP by design.
    }
    public targetFieldActionChanged(dataItem: CurrentRuleAction, eventData: any): void {
        // Reset any state-related flags.
        this.componentControlStateInformation.recomputeAddRuleConditionEnabled = true;
    }

    // Define methods called by the Telerik/Kendo grid used to edit the current rule's actions.
    public addRuleConditionClicked(eventData: any): void {        
        let newRuleCondition: CurrentRuleCondition = new CurrentRuleCondition();
        newRuleCondition.formTemplate = this.data.form;
        this.currentRuleAction.conditions.push(newRuleCondition);
    }
    public saveRuleClicked(eventData: any): void {
        let ruleActionClone: CurrentRuleAction = this.currentRuleAction.clone();
        ruleActionClone.setNoDefaultAttributesToNull();
        // Note:  if multiple target fields are selected, we have to replace the fields with a single, multiple fields metadata field.
        //let fieldRepresentingOtherFields: FormField = ruleActionClone.createMultipleFieldsMetadataFieldIfApplicable();
        let fieldRepresentingOtherFieldInfo: MultipleFieldsMetadataFieldInfo = ruleActionClone.createMultipleFieldsMetadataFieldIfApplicable();
        if (fieldRepresentingOtherFieldInfo != null) {
            if (!fieldRepresentingOtherFieldInfo.fieldAlreadExisted) {
                let fieldRepresentingOtherFields: FormField = fieldRepresentingOtherFieldInfo.multipleFieldsMetadataField;

                // Let's give this field a name and display name and add it to the form.
                let currentForm: Form = <Form>this.data.form;
                //let fieldRepresentingOtherFieldsName: string = `Representative Field ${currentForm.formFields.length + 1}`;
                let fieldRepresentingOtherFieldsName: string = `Representative_Field_${currentForm.formFields.length + 1}`;
                fieldRepresentingOtherFields.name = fieldRepresentingOtherFieldsName;
                fieldRepresentingOtherFields.displayName = fieldRepresentingOtherFieldsName;

                currentForm.formFields.push(fieldRepresentingOtherFields);
            }
        }
        // If we have a child form field, make it the target form field.
        else if (ruleActionClone.childFormFieldId > 0) {
            // Make this child field the target field.
            //ruleActionClone.targetFieldIds = [ruleActionClone.childFormFieldId];
            let childFormField = ruleActionClone.availableChildFormFields.find(ff => ff.id == ruleActionClone.childFormFieldId);
            //ruleActionClone.targetFormFields = [childFormField];
            if (childFormField != null) {
                ruleActionClone.childFieldName = childFormField.name;
                let fieldDef: FieldDefinition = this.fieldDefinitionService.getFieldDefinition(childFormField.fieldDefinitionClassName);
                ruleActionClone.filterOperator = fieldDef.customLogicHandler.filterOperator();
            }               
        }

        if (this.currentRuleAction.existingRuleActionIndex >= 0) {
            this.allRuleActions.replaceRuleAction(this.currentRuleAction.existingRuleActionIndex, ruleActionClone);
        }
        else {
            this.allRuleActions.addRuleAction(ruleActionClone);
        }            

        this.currentRuleAction.resetToDefaults();
        this.componentControlStateInformation.addRuleConditionEnabled = false;
        this.componentControlStateInformation.recomputeAddRuleConditionEnabled = false;
    }

    private static getIndexOfExistingRule(form: Form, existingRule: FormFieldExpression): number {
        let ruleIndex: number = -1;

        for (let seekIndex: number = 0; seekIndex < form.conditionalLogicRules.length; seekIndex++) {
            let seekRule: FormFieldExpression = form.conditionalLogicRules[seekIndex];

            if (seekRule == existingRule) {
                ruleIndex = seekIndex;
                break;
            }
        }

        return ruleIndex;
    }

    public deleteRuleConditionClicked(event: RemoveEvent): void {
        let dataItem: CurrentRuleCondition = <CurrentRuleCondition>event.dataItem;
        if (this.currentRuleAction.conditions != null)
            this.currentRuleAction.conditions = this.currentRuleAction.conditions.filter(a => a != dataItem);
    }
    public ruleConditionCellClicked(eventData: any): void {
        // Note:  this method presently performs no work by design.
    }
    public ruleConditionCellClosed(eventData: any): void {
        // Note:  this method presently performs no work by design.
    }
    public ruleConditionRowsReordered(eventData: any): void {
        // Note:  this method presently performs no work by design.
    }


    // Define methods called by the Telerik/Kendo grid used to edit existing conditional logic rules.
    public editExistingRuleActionClicked(eventData: any): void {
        let dataItem: CurrentRuleAction = <CurrentRuleAction>eventData['dataItem'];

        let ruleIndex: number = this.allRuleActions.getExistingRuleIndex(dataItem);
        let ruleActionClone: CurrentRuleAction = dataItem.clone();
        // If we have a target field that represents other target fields, expand that field now.
        ruleActionClone.expandMultipleFieldsMetadataFieldIfApplicable(<Form>this.data.form);
        let currentForm: Form = <Form>this.data.form;
        let availableOperators: ConditionalLogicRuleOperator[] = this.fieldConditionalLogicService.getAvailableFieldOperators();
        let editSuccess: boolean = this.currentRuleAction.editExistingRuleAction(currentForm, ruleActionClone, availableOperators, this.formBuilderService);
        this.targetFieldChanged(this.currentRuleAction, null);
        //this.childFieldNameChanged(this.currentRuleAction, null);
        this.currentRuleAction.existingRuleActionIndex = ruleIndex;
    }
    public deleteExistingRuleClicked(eventData: any): void {
        let dataItem: CurrentRuleAction = <CurrentRuleAction>eventData['dataItem'];
        let ruleIndex: number = this.allRuleActions.getExistingRuleIndex(dataItem);

        this.allRuleActions.removeRuleAction(dataItem);

        if (ruleIndex == this.currentRuleAction.existingRuleActionIndex)
            this.currentRuleAction.resetToDefaults();
    }
    public existingRuleCellClicked(eventData: any): void {
        // Note:  this method presently performs no work by design.
    }
    public existingRuleCellClosed(eventData: any): void {
        // Note:  this method presently performs no work by design.
    }
    public existingRulesRowsRecordered(eventData: any): void {
        // Note:  this method presently performs no work by design.
    }

    public saveClicked(): void {
        let currentForm: Form = <Form>this.data.form;
        let availableOperators: ConditionalLogicRuleOperator[] = this.fieldConditionalLogicService.getAvailableFieldOperators();
        currentForm.conditionalLogicRules = this.allRuleActions.convertToFormFieldExpressions(currentForm, this.allFieldInfo.metadataFieldsAsFauxFormFields(), availableOperators);
      
        this.allRuleActions.addAnyUsedMetadataFieldsNotYetInForm(currentForm, this.allFieldInfo.metadataFieldsAsFauxFormFields());
        this.allRuleActions.removeAllRuleActions();
        this.dialogRef.close(currentForm);
    }
    public cancelClicked(): void {
        this.dialogRef.close(null);
    }

    // Helper methods.
    private static convertExistingRuleToSingleActionRules(form: Form, metadataFauxFormFields: FormField[], conditionalLogicRules: FormFieldExpression[], availableOperators: ConditionalLogicRuleOperator[], formBuilderService: FormBuilderService): CurrentRuleAction[] {
        let convertedRules: FormFieldExpression[] = [];

        // Create single action rules.
        for (let ruleIndex: number = 0; ruleIndex < conditionalLogicRules.length; ruleIndex++) {
            let existingRule: FormFieldExpression = conditionalLogicRules[ruleIndex];

            // If the current rule only has one action, no need to convert it.
            if (existingRule.actions.length == 1) {
                convertedRules.push(existingRule);
            }                
            else {
                // Need to convert this rule.
                for (let actionIndex: number = 0; actionIndex < existingRule.actions.length; actionIndex++) {
                    let existingAction: FormFieldAction = existingRule.actions[actionIndex];

                    let convertedRule: FormFieldExpression = new FormFieldExpression();

                    convertedRule.sourceFieldId = existingRule.sourceFieldId;
                    convertedRule.sourceFieldName = existingRule.sourceFieldName;
                    convertedRule.sourceFieldChildFieldName = existingRule.sourceFieldChildFieldName;
                    convertedRule.expressionOrder = existingRule.expressionOrder + actionIndex;
                    convertedRule.operatorSymbol = existingRule.operatorSymbol;
                    convertedRule.operandValue = existingRule.operandValue;

                    let convertedAction: FormFieldAction = new FormFieldAction();
                    convertedAction.targetFieldId = existingAction.targetFieldId;
                    convertedAction.targetFieldName = existingAction.targetFieldName;
                    convertedAction.actionOrder = 0;
                    convertedRule.actions = [convertedAction];

                    convertedRules.push(convertedRule);
                }
            }
        }

        // Group rules by target field Id and action.
        let groupedRules: CurrentRuleAction[] = FieldConditionalLogicComponent.groupRulesByTargetFieldIdAndAction(form, metadataFauxFormFields, convertedRules, availableOperators, formBuilderService);

        // Return the converted rules (which may be in the state as the existing form rules).
        return groupedRules;
    }
    private static groupRulesByTargetFieldIdAndAction(form: Form, metadataFauxFormFields: FormField[], conditionalLogicRules: FormFieldExpression[], availableOperators: ConditionalLogicRuleOperator[], formBuilderService: FormBuilderService): CurrentRuleAction[] {
        // Note:  rules passed to this method already have a single acdtion.
        let groupedRules: CurrentRuleAction[] = [];
        let mapOfRulesByTargetFieldIdAndAction: IRulesByTargetFieldIdAndAction = {};

        for (let ruleIndex: number = 0; ruleIndex < conditionalLogicRules.length; ruleIndex++) {
            let rule: FormFieldExpression = conditionalLogicRules[ruleIndex];

            if ((rule.actions == null) || (rule.actions.length != 1))
                throw `FieldConditionalLogicComponent.groupRulesByTargetFieldIdAndAction():  encountered a rule in an inconsistent state.`;

            let action: FormFieldAction = rule.actions[0];
            let targetIdAndActionKey: string = (action.childFieldName != null) && (action.childFieldName.trim() != '') ? `${action.targetFieldId}.${action.childFieldName}.${action.actionName}` : `${action.targetFieldId}.${action.actionName}`;
            let existingRuleWithTargetIdAndAction: CurrentRuleAction = mapOfRulesByTargetFieldIdAndAction[targetIdAndActionKey];

            if (existingRuleWithTargetIdAndAction != null) {
                let condition: CurrentRuleCondition = CurrentRuleCondition.createConditionFromRule(form, metadataFauxFormFields, rule, availableOperators);
                existingRuleWithTargetIdAndAction.conditions.push(condition);
            } else {
                let ruleAction: CurrentRuleAction = CurrentRuleAction.createFromSingleActionRule(form, metadataFauxFormFields, rule, availableOperators, formBuilderService);
                groupedRules.push(ruleAction);

                mapOfRulesByTargetFieldIdAndAction[targetIdAndActionKey] = ruleAction;
            }                
        }

        return groupedRules;
    }
}
